import {
  type CreateTicketBody,
  MAX_TICKET_ATTACHMENT_BYTES,
  MAX_TICKET_ATTACHMENTS,
  TICKET_INLINE_IMAGE_TYPES,
  type TicketAttachmentDTO,
} from '@hearth/shared';
import { clsx } from 'clsx';
import { FileText, ImageIcon, Paperclip, X } from 'lucide-react';
import { type DragEvent, type ReactNode, useRef, useState } from 'react';
import { Button, IconButton } from '../../components/ui';
import { formatFileSize } from '../../lib/format';

const INLINE_TYPES: readonly string[] = TICKET_INLINE_IMAGE_TYPES;

/**
 * The body of a new ticket or reply: plain JSON when nothing is attached (as always), multipart when files are — the
 * text fields first, then every file in `files`.
 */
export function ticketRequest<T extends CreateTicketBody>(fields: T, files: readonly File[]): T | FormData {
  if (files.length === 0) return fields;
  const form = new FormData();
  if (fields.kind !== undefined) form.append('kind', fields.kind);
  if (fields.title !== undefined && fields.title !== null) form.append('title', fields.title);
  form.append('body', fields.body);
  form.append('dictated', String(fields.dictated ?? false));
  for (const file of files) form.append('files', file);
  return form;
}

// Only file drags count: dragging selected text or a link over the form must not light it up.
const draggingFiles = (event: DragEvent) => event.dataTransfer.types.includes('Files');

/**
 * Wraps a message form: files dragged over it light it up and are attached when dropped, and files pasted into it
 * (a screenshot, say) are attached instead of pasted.
 */
export function AttachmentDropZone({
  onFiles,
  disabled = false,
  className,
  children,
}: {
  onFiles: (files: Iterable<File>) => void;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}) {
  // Moving across child elements fires enter/leave for each of them; counting keeps the highlight from flickering.
  const [depth, setDepth] = useState(0);
  return (
    <div
      className={clsx('relative', className)}
      onDragEnter={(event) => {
        if (!draggingFiles(event)) return;
        event.preventDefault();
        if (!disabled) setDepth((d) => d + 1);
      }}
      onDragOver={(event) => {
        if (!draggingFiles(event)) return;
        // Without this the browser would open the dropped file in place of the app.
        event.preventDefault();
        event.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
      }}
      onDragLeave={(event) => {
        if (draggingFiles(event)) setDepth((d) => Math.max(0, d - 1));
      }}
      onDrop={(event) => {
        if (!draggingFiles(event)) return;
        event.preventDefault();
        setDepth(0);
        if (!disabled) onFiles(event.dataTransfer.files);
      }}
      onPaste={(event) => {
        if (disabled || event.clipboardData.files.length === 0) return;
        event.preventDefault();
        onFiles(event.clipboardData.files);
      }}
    >
      {children}
      {depth > 0 && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-ember-400 bg-ember-50/90 text-[13px] font-medium text-ember-800"
        >
          <Paperclip size={16} aria-hidden />
          Drop files to attach
        </div>
      )}
    </div>
  );
}

/** "Attach files" plus the files chosen so far, each removable. */
export function AttachmentPicker({
  files,
  onAdd,
  onRemove,
  disabled = false,
}: {
  files: readonly File[];
  onAdd: (files: Iterable<File>) => void;
  onRemove: (index: number) => void;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Button variant="ghost" size="sm" icon={Paperclip} disabled={disabled} onClick={() => input.current?.click()}>
          Attach files
        </Button>
        <input
          ref={input}
          type="file"
          multiple
          aria-label="Attach files"
          tabIndex={-1}
          disabled={disabled}
          className="sr-only"
          onChange={(event) => {
            onAdd(event.target.files ?? []);
            // Cleared so picking the same file again (after removing it) still fires a change.
            event.target.value = '';
          }}
        />
        <span className="text-xs text-slate-500">
          or drop files here · up to {MAX_TICKET_ATTACHMENTS} files, {MAX_TICKET_ATTACHMENT_BYTES / 1024 / 1024} MB each
        </span>
      </div>
      {files.length > 0 && (
        <ul aria-label="Files to send" className="flex flex-wrap gap-1.5">
          {files.map((file, index) => {
            const Icon = file.type.startsWith('image/') ? ImageIcon : FileText;
            return (
              <li
                key={`${index}-${file.name}`}
                className="inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-md border border-slate-200 bg-surface py-0.5 pr-0.5 pl-2 text-[13px] text-slate-700"
              >
                <Icon size={14} aria-hidden className="shrink-0 text-slate-400" />
                <span className="max-w-56 truncate" title={file.name}>
                  {file.name}
                </span>
                <span className="shrink-0 text-xs text-slate-500 tabular">{formatFileSize(file.size)}</span>
                <IconButton icon={X} label={`Remove ${file.name}`} size="sm" disabled={disabled} onClick={() => onRemove(index)} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Files of one message in the thread: images as thumbnails (opened full size in a new tab), anything else as a download. */
export function MessageAttachments({ ticketId, attachments }: { ticketId: string; attachments: readonly TicketAttachmentDTO[] }) {
  if (attachments.length === 0) return null;
  return (
    <ul aria-label="Attachments" className="mt-3 flex flex-wrap items-end gap-2">
      {attachments.map((file) => {
        const href = `/api/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(file.id)}`;
        const label = `${file.filename} · ${formatFileSize(file.size)}`;
        return (
          <li key={file.id} className="max-w-full min-w-0">
            {INLINE_TYPES.includes(file.contentType) ? (
              <a
                href={href}
                target="_blank"
                rel="noopener"
                title={label}
                className="block rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember-500"
              >
                <img src={href} alt={file.filename} loading="lazy" className="max-h-40 max-w-full rounded border border-slate-200 bg-surface object-contain" />
              </a>
            ) : (
              <a
                href={href}
                download={file.filename}
                title={label}
                className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-slate-200 bg-surface px-2 py-1 text-[13px] text-slate-700 transition-colors hover:border-slate-300 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember-500"
              >
                <FileText size={14} aria-hidden className="shrink-0 text-slate-400" />
                <span className="truncate">{file.filename}</span>
                <span className="shrink-0 text-xs text-slate-500 tabular">{formatFileSize(file.size)}</span>
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );
}
